import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { colors } from "app/popleads/components/ui-kit/colors";
import useGetRecentNotes from "app/popleads/hooks/useGetRecentNotes";
import ExtensionCard from "app/popleads/components/ui-kit/ExtensionCard";
import moment from "moment";
import { StyledCardContent, StyledCardFooter } from "./common/styled";
import { ExtensionBody, ExtensionSmall, Heading2 } from "app/popleads/components/ui-kit/Text";
import useGetRecentPersonalActivity from "app/popleads/hooks/useGetRecentPersonalActivity";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "_metronic/_helpers";
import useGetCompanyUsers from "app/popleads/hooks/useGetCompanyUsers";
import CustomSelect, { StyledCheckbox } from "app/popleads/components/ui-kit/Select";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import FloatingButtons from "app/popleads/components/ui-kit/FloatingButtons";
import { Link } from "react-router-dom";
import { POPUP_CONTENTS } from "app/popleads/modules/extension/utils/constants";
import { useExtensionContext } from "../ExtensionContext";
import highlightFromTo from "../utils/highlightFromTo";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import InputWithIcon from "app/popleads/components/ui-kit/Input";
import useInviteUser from "app/popleads/hooks/useInviteUser";
import { useSelector } from "react-redux";
import { Spinner } from "react-bootstrap";
import { ShowMoreButton } from "./ExtensionNote";

const FeedContainer = styled.div`
    padding: 0px 12px;
`;

const FeedTabs = styled.div`
    display: flex;
    gap: 20px;
    border-bottom: 1px solid ${colors.neutral[200]};
`;

const FeedTabsItem = styled.span`
    color: ${({ isActive }) => (isActive ? colors.l3rnTech[900] : colors.neutral[700])};
    font-size: 12px;
    font-weight: 500;
    line-height: 22px;
    cursor: pointer;
    padding: 0 8px;
    height: 28px;
    margin-bottom: -1px;
    ${({ isActive }) => isActive && `border-bottom: 1px solid ${colors.l3rnTech[900]};`}
    white-space: nowrap;
`;

const StyledContentWrapper = styled.div`
    overflow-y: auto;
    height: calc(100% - 90px);
    padding-bottom: 16px;

    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: ${colors.neutral[100]};
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background-color: ${colors.neutral[400]};
        border-radius: 4px;
        min-height: 20px;
        border: 1px solid ${colors.neutral[200]};
    }

    &::-webkit-scrollbar-thumb:hover {
        background-color: ${colors.neutral[600]};
    }

    &::-webkit-scrollbar-corner {
        background: ${colors.neutral[100]};
    }

    /* Firefox scrollbar */
    scrollbar-width: thin;
    scrollbar-color: ${colors.neutral[400]} ${colors.neutral[100]};
`;

const StyledCardItemContent = styled.div`
    font-size: 14px;
    display: flex;
    gap: 8px;
    align-items: center;
`;

const StyledCardText = styled(ExtensionBody)`
    color: ${({ variant }) => (variant ? colors[variant][800] : colors.neutral[800])};
    font-weight: 400;
    overflow-wrap: anywhere;

    & p {
        margin: 0;
    }

    & i {
        color: inherit;
        font-size: inherit;
        font-weight: inherit;
        letter-spacing: inherit;
        line-height: inherit;
    }
`;

const Avatar = styled.img`
    border-radius: 50%;
    width: 24px;
    height: 24px;
`;

const AvatarSvg = styled(SVG)`
    border-radius: 50%;
    min-width: 24px;
    min-height: 24px;
`;

const StayledLink = styled.a`
    font-size: 16px;
    color: ${colors.l3rnTech[900]};
    text-decoration: none;
    &:hover {
        color: inherit;
    }
`;

const ClippedText = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
`;

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #384d567a;
    z-index: 999;
`;

const StyledModalContainer = styled.div`
    position: absolute;
    top: 0;
    z-index: 1000;
    padding: 0 8px;
    width: 100%;
`;

const StyledModal = styled.div`
    border: 1px solid #ccc;
    border-radius: 8px;
    background: #fff;
    padding: 12px 0;
`;

const ModalHeader = styled.div`
    // display: flex;
    padding: 0 16px 0 16px;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 16px;
    border-top: 1px solid ${colors.neutral[100]};
`;
const StyledShowMoreButton = styled(ShowMoreButton)`
    margin-top: 4px;
    margin-bottom: 4px;
`;
const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    max-height: 350px;
    overflow-y: auto;
    padding: 0 16px;

    /* General scrollbar style */
    &::-webkit-scrollbar {
        width: 9px; /* Width of the scrollbar */
    }

    /* Scrollbar thumb style */
    &::-webkit-scrollbar-thumb {
        background-color: ${colors.neutral[500]}; /* Color of the scrollbar thumb */
        border-radius: 9px; /* Rounded corners */
        border: 3px solid transparent;
        background-clip: content-box;
        min-height: 58px;
    }

    /* Thumb color on hover */
    &::-webkit-scrollbar-thumb:hover {
        background-color: ${colors.neutral[700]};
    }

    /* Scrollbar corner style (if any) */
    &::-webkit-scrollbar-corner {
        background-color: ${colors.neutral[100]};
    }
`;

const ModalItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const SelectAll = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;

    padding-right: ${({ hasScrollbar }) => (hasScrollbar ? "9px;" : "0px")};
    margin-bottom: 18px;

    @supports (-moz-appearance: none) {
        padding-right: 0px;
    }
`;

const ModalText = styled(ExtensionBody)`
    color: ${colors.neutral[800]};
`;

const CustomCheckbox = styled(StyledCheckbox)`
    ${({ marker }) =>
        marker === "em-dash" &&
        `&:checked::after {
        content: "—";
    }`}
`;

const StickyFooter = styled.div`
    width: 100%;
    padding: 0 20px;
    position: absolute;
    bottom: 20px;
    text-align: right;
`;

const InfoText = styled.div`
    font-size: 10px;
    font-weight: 400;
    line-height: 20px;
    color: ${colors.l3rnTech[900]};
    margin-bottom: 8px;
`;

const StyledForm = styled(Form)`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 12px;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    height: 100%;
`;

const ExtensionFeedList = () => {
    const [selectedTab, setSelectedTab] = useState("Recent Notes");
    const [recentNoteList, setRecentNoteList] = useState([]);
    const [recentActivityList, setRecentActivityList] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [paramUsers, setParamUsers] = useState([]);
    const [isOpenUsersModal, setIsOpenUsersModal] = useState(false);
    const [isOpenNewUserModal, setIsOpenNewUserModal] = useState(false);
    const { recentNotes, loading: loadingRecentNotes } = useGetRecentNotes({ users: paramUsers });
    const { recentActivity, loading: loadingRecentActivity } = useGetRecentPersonalActivity();
    const { companyUsers } = useGetCompanyUsers({ type: "byRoles" });
    const { companyUsers: allCompanyUsers } = useGetCompanyUsers();
    const options = [{ value: "Recent Notes" }, { value: "My Recent Actions" }];
    const { setState, feed, setFeed, liveActivities, setLiveActivities } = useExtensionContext();
    const { user: loggedInUser } = useSelector((state) => state.auth);

    const { inviteUser } = useInviteUser();

    const companyUserIds = useMemo(() => {
        return companyUsers.map((user) => user.id);
    }, [companyUsers]);

    const [expandedNotes, setExpandedNotes] = useState({});
    const MAX_NOTE_LENGTH = 180;

    const handleInviteUser = async (values) => {
        await inviteUser(values);
        setIsOpenNewUserModal(false);
    };

    const handleToggleExpand = (noteKey) => {
        setExpandedNotes((prev) => ({
            ...prev,
            [noteKey]: !prev[noteKey],
        }));
    };

    const generateContent = (variant, hasIcon, hasHeaderIcon, title, profile, time, noteCreator, noteKey) => {
        const isLong = typeof title === "string" && title.length > MAX_NOTE_LENGTH;
        const isExpanded = expandedNotes[noteKey];
        const displayText = isLong && !isExpanded ? title.slice(0, MAX_NOTE_LENGTH) + "..." : title;
        return {
            icon: hasIcon && (
                <StyledCardContent
                    variant={"warning"}
                    className="material-symbols-outlined"
                    style={{ fontSize: "22px", fontWeight: "300" }}
                >
                    sync_problem
                </StyledCardContent>
            ),
            header: (
                <StyledCardText variant={variant}>
                    {hasHeaderIcon && (
                        <span style={{ width: "24px", textAlign: "center", marginRight: "8px" }}>
                            <SVG
                                src={toAbsoluteUrl("/media/svg/icons/Extension/pop-it.svg")}
                                style={{ width: "16px", height: "13px", fill: "currentColor" }}
                            />
                        </span>
                    )}
                    <span
                        dangerouslySetInnerHTML={{
                            __html: variant === "warning" ? highlightFromTo(displayText) : displayText,
                        }}
                    />
                    {isLong && (
                        <StyledShowMoreButton onClick={() => handleToggleExpand(noteKey)}>
                            {isExpanded ? "Show Less" : "Show More"}
                        </StyledShowMoreButton>
                    )}
                </StyledCardText>
            ),
            content: profile && (
                <div>
                    <StyledCardItemContent>
                        {profile?.resim ? (
                            <Avatar
                                src={profile?.resim}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = toAbsoluteUrl("/media/svg/icons/Extension/user-avatar.svg");
                                }}
                            />
                        ) : (
                            <AvatarSvg src={toAbsoluteUrl("/media/svg/icons/Extension/user-avatar.svg")} />
                        )}
                        <ClippedText>
                            {profile?.ad} {profile?.soyad}
                        </ClippedText>
                        <StayledLink
                            href={`/extension?profileId=${profile?.id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="material-symbols-outlined"
                        >
                            open_in_new
                        </StayledLink>
                    </StyledCardItemContent>
                    <ExtensionSmall style={{ overflowWrap: "anywhere" }}>
                        {`${profile?.baslik ?? ""}${
                            profile?.baslik && profile?.sirket ? " at " : ""
                        }${profile?.sirket ?? ""}`}
                    </ExtensionSmall>
                </div>
            ),
            footer: (
                <StyledCardFooter>
                    {`${moment(time).format("ddd, DD MMM YYYY [at] HH:mm")} ${
                        noteCreator ? `| Note by ${noteCreator}` : ""
                    }`}
                </StyledCardFooter>
            ),
        };
    };

    useEffect(() => {
        const noteList = [...recentNotes].reverse()?.map((note, idx) => {
            const variant =
                note.tip === "otomatik" && note.metin === "New Lead"
                    ? "success"
                    : note.tip === "otomatik"
                    ? "warning"
                    : null;
            const hasIcon = note.tip === "otomatik";
            const hasHeaderIcon = note.metin === "New Lead" || note.metin === "New Contact";
            const noteKey = note.id || idx;
            return generateContent(
                variant,
                hasIcon,
                hasHeaderIcon,
                note.metin,
                note.profil,
                note.zaman,
                note.kayitci,
                noteKey
            );
        });

        setRecentNoteList(noteList);
    }, [recentNotes, expandedNotes]);

    useEffect(() => {
        const activityList = recentActivity?.map((activity) => {
            const variant = activity.title.includes("Lead") ? "success" : "warning";
            const hasIcon = true;
            const hasHeaderIcon = activity.title === "New Lead" || activity.title === "New Contact";
            return generateContent(variant, hasIcon, hasHeaderIcon, activity.title, activity.profil[0], activity.time);
        });

        setRecentActivityList(activityList);
    }, [recentActivity]);

    useEffect(() => {
        if (
            feed.length > 0 &&
            !loadingRecentNotes &&
            feed[feed.length - 1]?.id !== recentNotes[recentNotes.length - 1]?.id
        ) {
            const isLoggedInUserSelected = selectedUsers?.includes(loggedInUser?.id);
            if (isLoggedInUserSelected || selectedUsers.length === 0) {
                const noteList = feed.reverse()?.map((note) => {
                    const variant =
                        note.tip === "otomatik" && note.metin === "New Lead"
                            ? "success"
                            : note.tip === "otomatik"
                            ? "warning"
                            : null;
                    const hasIcon = note.tip === "otomatik";
                    const hasHeaderIcon = note.metin === "New Lead" || note.metin === "New Contact";
                    return generateContent(
                        variant,
                        hasIcon,
                        hasHeaderIcon,
                        note.metin,
                        note.profil,
                        note.zaman,
                        note.kayitci
                    );
                });
                setRecentNoteList((prevNotes) => [...noteList, ...prevNotes]);
            }
            setFeed([]);
        }
    }, [feed, recentNotes, loadingRecentNotes, selectedUsers]);

    useEffect(() => {
        if (liveActivities.length > 0) {
            const activityList = liveActivities?.map((activity) => {
                const variant = activity.title.includes("Lead") ? "success" : "warning";
                const hasIcon = true;
                const hasHeaderIcon = activity.title === "New Lead" || activity.title === "New Contact";
                return generateContent(
                    variant,
                    hasIcon,
                    hasHeaderIcon,
                    activity.title,
                    activity.profil[0],
                    activity.time
                );
            });
            setRecentActivityList((prevActivities) => [...activityList, ...prevActivities]);
            setLiveActivities([]);
        }
    }, [liveActivities]);

    return (
        <>
            {isOpenUsersModal && (
                <>
                    <Overlay
                        onClick={() => {
                            setIsOpenUsersModal(false);
                            setSelectedUsers(paramUsers);
                        }}
                    />
                    <StyledModalContainer>
                        <StyledModal>
                            <ModalHeader>
                                <InfoText>Only the notes from the selected users will be displayed</InfoText>
                                <SelectAll hasScrollbar={companyUsers.length > 11}>
                                    <ModalText>Select All</ModalText>
                                    <CustomCheckbox
                                        variant="l3rnTech"
                                        checked={selectedUsers.length > 0}
                                        marker={selectedUsers.length !== companyUsers.length ? "em-dash" : "check"}
                                        onClick={(e) => {
                                            if (e.target.checked) {
                                                setSelectedUsers(companyUserIds);
                                            } else {
                                                setSelectedUsers([]);
                                            }
                                        }}
                                    />
                                </SelectAll>
                            </ModalHeader>
                            <ModalContent>
                                {companyUsers?.map((user) => (
                                    <ModalItem key={user.id}>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            {user.linkedinPicture ? (
                                                <Avatar
                                                    src={user.linkedinPicture}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = toAbsoluteUrl(
                                                            "/media/svg/icons/Extension/user-avatar.svg"
                                                        );
                                                    }}
                                                />
                                            ) : (
                                                <AvatarSvg
                                                    src={toAbsoluteUrl("/media/svg/icons/Extension/user-avatar.svg")}
                                                />
                                            )}
                                            <ModalText style={{ marginLeft: "8px" }}>
                                                {user.id === loggedInUser?.id ? "Me" : user.name}
                                            </ModalText>
                                        </div>
                                        <StyledCheckbox
                                            variant="l3rnTech"
                                            checked={selectedUsers.includes(user.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedUsers([...selectedUsers, user.id]);
                                                } else {
                                                    setSelectedUsers(selectedUsers.filter((id) => id !== user.id));
                                                }
                                            }}
                                        />
                                    </ModalItem>
                                ))}
                            </ModalContent>
                            <ModalFooter>
                                <ButtonWithText
                                    variant="passive"
                                    buttonStyle="borderless"
                                    size="small"
                                    onClick={() => {
                                        setIsOpenUsersModal(false);
                                        setSelectedUsers(paramUsers);
                                    }}
                                >
                                    Cancel
                                </ButtonWithText>
                                <ButtonWithText
                                    variant="l3rnTech"
                                    buttonStyle="default"
                                    size="small"
                                    onClick={() => {
                                        setIsOpenUsersModal(false);
                                        setParamUsers(selectedUsers);
                                    }}
                                >
                                    OK
                                </ButtonWithText>
                            </ModalFooter>
                        </StyledModal>
                    </StyledModalContainer>
                </>
            )}

            {isOpenNewUserModal && (
                <>
                    <Overlay onClick={() => setIsOpenNewUserModal(false)} />
                    <StyledModalContainer>
                        <StyledModal>
                            <ModalHeader>
                                <Heading2>Add New User</Heading2>
                                <InfoText>
                                    Invite your team to use L3RN Tech. L3RN Tech is better when used together with whole
                                    team.
                                </InfoText>
                            </ModalHeader>
                            <ModalContent>
                                <Formik
                                    initialValues={{
                                        name: "",
                                        surname: "",
                                        email: "",
                                        managerName: "",
                                    }}
                                    validationSchema={Yup.object().shape({
                                        name: Yup.string().required("Name is required"),
                                        surname: Yup.string().required("Surname is required"),
                                        email: Yup.string()
                                            .email("Invalid email address")
                                            .required("Email is required"),
                                        managerName: Yup.string().required("Manager Name is required"),
                                    })}
                                    onSubmit={handleInviteUser}
                                >
                                    {({ setFieldValue, errors, touched }) => (
                                        <StyledForm>
                                            <Field
                                                name="name"
                                                render={({ field: { onChange, ...field } }) => (
                                                    <InputWithIcon
                                                        variant="l3rnTech"
                                                        error={errors.name && touched.name}
                                                        placeholder={"Name"}
                                                        debounceTime={0}
                                                        onChange={(e) => {
                                                            setFieldValue("name", e.target.value);
                                                        }}
                                                        {...field}
                                                    />
                                                )}
                                            />
                                            <Field
                                                name="surname"
                                                render={({ field: { onChange, ...field } }) => (
                                                    <InputWithIcon
                                                        variant="l3rnTech"
                                                        error={errors.surname && touched.surname}
                                                        placeholder={"Surname"}
                                                        debounceTime={0}
                                                        onChange={(e) => {
                                                            setFieldValue("surname", e.target.value);
                                                        }}
                                                        {...field}
                                                    />
                                                )}
                                            />
                                            <Field
                                                name="email"
                                                render={({ field: { onChange, ...field } }) => (
                                                    <InputWithIcon
                                                        variant="l3rnTech"
                                                        error={errors.email && touched.email}
                                                        placeholder={"Email Address"}
                                                        debounceTime={0}
                                                        onChange={(e) => {
                                                            setFieldValue("email", e.target.value);
                                                        }}
                                                        {...field}
                                                    />
                                                )}
                                            />
                                            {allCompanyUsers.length > 0 && (
                                                <CustomSelect
                                                    error={errors.managerName && touched.managerName}
                                                    variant={"l3rnTech"}
                                                    type="select"
                                                    loadOptions={(input, callback) => {
                                                        callback(
                                                            allCompanyUsers
                                                                .filter((user) =>
                                                                    user.name
                                                                        .toLowerCase()
                                                                        .includes(input.toLowerCase())
                                                                )
                                                                .map((item) => {
                                                                    return {
                                                                        label: item.name,
                                                                        value: item.id,
                                                                    };
                                                                })
                                                                .slice(0, 10)
                                                        );
                                                    }}
                                                    onChange={(selected) => {
                                                        if (selected) {
                                                            setFieldValue("managerName", selected.label);
                                                        } else {
                                                            setFieldValue("managerName", "");
                                                        }
                                                    }}
                                                    defaultOptions={true}
                                                    placeholder="Please select manager"
                                                    isClearable={true}
                                                    menuPosition={"fixed"}
                                                />
                                            )}
                                            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                                                <ButtonWithText
                                                    variant="passive"
                                                    buttonStyle="borderless"
                                                    size="small"
                                                    onClick={() => {
                                                        setIsOpenNewUserModal(false);
                                                    }}
                                                >
                                                    Cancel
                                                </ButtonWithText>
                                                <ButtonWithText
                                                    variant="l3rnTech"
                                                    buttonStyle="default"
                                                    type="submit"
                                                    size="small"
                                                >
                                                    Invite
                                                </ButtonWithText>
                                            </div>
                                        </StyledForm>
                                    )}
                                </Formik>
                            </ModalContent>
                        </StyledModal>
                    </StyledModalContainer>
                </>
            )}

            <FeedContainer>
                <FeedTabs>
                    {options.map((option, index) => (
                        <FeedTabsItem
                            key={index}
                            isActive={selectedTab === option.value}
                            onClick={() => setSelectedTab(option.value)}
                        >
                            {option.value}
                        </FeedTabsItem>
                    ))}
                    {selectedTab === "Recent Notes" && (
                        <FeedTabsItem
                            style={{ marginLeft: "auto" }}
                            onClick={() => {
                                setIsOpenUsersModal(true);
                                // setFilters([53]);
                            }}
                        >
                            <span
                                className={"material-symbols-outlined"}
                                style={{ fontSize: "20px", lineHeight: "18px" }}
                            >
                                person_search
                            </span>
                        </FeedTabsItem>
                    )}
                </FeedTabs>
            </FeedContainer>
            <StyledContentWrapper>
                <FeedContainer style={{ height: "100%" }}>
                    {selectedTab === "Recent Notes" ? (
                        loadingRecentNotes ? (
                            <LoadingContainer>
                                <Spinner animation="border" variant="l3rnTech" />
                            </LoadingContainer>
                        ) : (
                            <ExtensionCard
                                haveViewMore={false}
                                cardItems={recentNoteList}
                                errorDescription="No recent notes available."
                            />
                        )
                    ) : loadingRecentActivity ? (
                        <LoadingContainer>
                            <Spinner animation="border" variant="l3rnTech" />
                        </LoadingContainer>
                    ) : (
                        <ExtensionCard
                            haveViewMore={false}
                            cardItems={recentActivityList}
                            errorDescription="No recent actions available."
                        />
                    )}
                </FeedContainer>
                <StickyFooter>
                    <FloatingButtons variant="l3rnTech">
                        <ButtonWithText
                            variant="l3rnTech"
                            buttonStyle="default"
                            size="small"
                            onClick={() => {
                                setIsOpenNewUserModal(true);
                            }}
                        >
                            Add New User
                        </ButtonWithText>
                        <ButtonWithText
                            variant="l3rnTech"
                            buttonStyle="default"
                            size="small"
                            onClick={() => {
                                setState((prevState) => ({
                                    ...prevState,
                                    selectedTab: "Tasks",
                                    popupContent: POPUP_CONTENTS.NEW_TASK,
                                    popupIsOpen: true,
                                    taskPage: true,
                                }));
                            }}
                        >
                            Add New Task
                        </ButtonWithText>
                        <Link to="/activities?newActivity=true" target="_blank">
                            <ButtonWithText
                                variant="l3rnTech"
                                buttonStyle="default"
                                size="small"
                                style={{ width: "100%", cursor: "pointer" }}
                            >
                                Add New Activity
                            </ButtonWithText>
                        </Link>
                        <ButtonWithText
                            variant="l3rnTech"
                            buttonStyle="default"
                            size="small"
                            onClick={() => {
                                setState((prevState) => ({
                                    ...prevState,
                                    selectedTab: "New Lead",
                                }));
                            }}
                        >
                            Generate New Lead
                        </ButtonWithText>
                    </FloatingButtons>
                </StickyFooter>
            </StyledContentWrapper>
        </>
    );
};

export default ExtensionFeedList;
